import { FormEvent, useEffect, useRef, useState } from "react";
import axios from "axios";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import {
  createMenuItem,
  updateMenuItem,
  deleteMenuItem,
  searchMenuItems,
  MenuItemSearchQuery,
} from "services/mawaidMenuService";
import { CookingPot, Pencil, Plus, Save, Search, Trash2 } from "lucide-react";
import { usePageHeader } from "contexts/PageHeaderContext";
import { Badge } from "ui/badge";
import { Button } from "ui/button";
import { Input } from "ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "ui/select";
import { InputFieldset } from "ui/inputFieldset";
import { Switch } from "ui/switch";
import { Label } from "ui/label";
import ConfirmDialog from "ui/ConfirmDialog";
import Pagination from "ui/pagination";
import useDebounce from "hooks/useDebounce";
import { FullScreenLoading } from "components/ui/loader";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "ui/table";

type MenuItem = {
  publicId: string;
  description: string;
  name: string;
  code: string;
  status: "Active" | "Inactive";
};
type MenuForm = Omit<MenuItem, "publicId">;
const emptyForm: MenuForm = {
  name: "",
  code: "",
  description: "",
  status: "Active",
};

const getApiErrorMessage = (error: unknown, fallback: string) => {
  const message = axios.isAxiosError<{ message?: unknown }>(error)
    ? error.response?.data?.message
    : error instanceof Error
      ? error.message
      : undefined;
  return typeof message === "string" && message.trim() ? message : fallback;
};

const getSuccessMessage = (response: unknown, fallback: string) => {
  if (
    response &&
    typeof response === "object" &&
    "message" in response &&
    typeof response.message === "string" &&
    response.message.trim()
  )
    return response.message;
  return fallback;
};

export function MenuMasterPage() {
  const { setPageHeader } = usePageHeader();
  const [items, setItems] = useState<MenuItem[]>([]);
  const [filters, setFilters] = useState<MenuItemSearchQuery>({
    PageNumber: 1,
    PageSize: 10,
  });
  const debouncedFilters = useDebounce(filters, 400);
  const [totalRecords, setTotalRecords] = useState(0);
  const [listLoading, setListLoading] = useState(true);
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [loading, setLoading] = useState(false);
  const requestInFlight = useRef(false);

  useEffect(() => {
    const controller = new AbortController();
    const loadMenuItems = async () => {
      setListLoading(true);
      try {
        const result = await searchMenuItems(
          {
            ...debouncedFilters,
            Name: debouncedFilters.Name?.trim() || undefined,
            Code: debouncedFilters.Code?.trim() || undefined,
          },
          controller.signal,
        );
        if (controller.signal.aborted) return;
        const lastPage = Math.max(
          1,
          Math.ceil(result.totalRecords / debouncedFilters.PageSize),
        );
        if (debouncedFilters.PageNumber > lastPage) {
          setFilters((current) =>
            current === debouncedFilters
              ? { ...current, PageNumber: lastPage }
              : current,
          );
          return;
        }
        setItems(
          result.data.map((item) => ({
            publicId: item.publicId,
            name: item.name,
            code: item.code,
            description: item.description ?? "",
            status: item.isActive ? "Active" : "Inactive",
          })),
        );
        setTotalRecords(result.totalRecords);
      } catch (error) {
        if (!controller.signal.aborted) {
          toast.error(getApiErrorMessage(error, "Failed to load menu items"));
          setItems([]);
          setTotalRecords(0);
        }
      } finally {
        if (!controller.signal.aborted) setListLoading(false);
      }
    };
    void loadMenuItems();
    return () => controller.abort();
  }, [debouncedFilters, refreshVersion]);

  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<MenuForm>(emptyForm);
  const [errors, setErrors] = useState({ name: "", code: "" });
  const [deletingItem, setDeletingItem] = useState<MenuItem | null>(null);
  const nameInput = useRef<HTMLInputElement>(null);
  const codeInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setPageHeader({ title: "Menu Item", icon: <CookingPot /> });
    return () => setPageHeader(null);
  }, [setPageHeader]);

  const closeForm = () => {
    setOpen(false);
    setEditingId(null);
    setForm(emptyForm);
    setErrors({ name: "", code: "" });
  };
  const openForm = (item?: MenuItem) => {
    setEditingId(item?.publicId ?? null);
    setForm(
      item
        ? {
            name: item.name,
            code: item.code,
            description: item.description,
            status: item.status,
          }
        : emptyForm,
    );
    setErrors({ name: "", code: "" });
    setOpen(true);
  };

  const saveItem = async (event: FormEvent) => {
    event.preventDefault();
    if (requestInFlight.current) return;
    const name = form.name.trim();
    const code = form.code.trim();
    const nextErrors = {
      name: name ? "" : "Menu Name is required.",
      code: !code
        ? "Menu Code is required."
        : items.some(
              (item) =>
                item.publicId !== editingId &&
                item.code.toLowerCase() === code.toLowerCase(),
            )
          ? "Menu Code already exists."
          : "",
    };
    setErrors(nextErrors);
    if (nextErrors.name || nextErrors.code) {
      (nextErrors.name ? nameInput : codeInput).current?.focus();
      return;
    }
    requestInFlight.current = true;
    setLoading(true);
    try {
      const payload = {
        name,
        code,
        description: form.description.trim(),
        isActive: form.status === "Active",
      };
      const response =
        editingId !== null
          ? await updateMenuItem(editingId, payload)
          : await createMenuItem(payload);
      toast.success(
        getSuccessMessage(
          response,
          editingId === null
            ? "Menu item created successfully"
            : "Menu item updated successfully",
        ),
      );
      closeForm();
      setRefreshVersion((version) => version + 1);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to save menu item"));
    } finally {
      requestInFlight.current = false;
      setLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!deletingItem || requestInFlight.current) return;
    requestInFlight.current = true;
    setLoading(true);
    try {
      const response = await deleteMenuItem(deletingItem.publicId);
      toast.success(
        getSuccessMessage(response, "Menu item deleted successfully"),
      );
      setDeletingItem(null);
      setRefreshVersion((version) => version + 1);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to delete menu item"));
    } finally {
      requestInFlight.current = false;
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 pb-8">
      {(loading || listLoading) &&
        createPortal(
          <div className="fixed inset-0 z-[9999]">
            <FullScreenLoading />
          </div>,
          document.body,
        )}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-6">
        <div className="relative flex-1">
          <Search
            aria-hidden="true"
            className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-neutral-400"
          />
          <Input
            aria-label="Search by name"
            placeholder="Search by name"
            value={filters.Name ?? ""}
            onChange={(event) => {
              setFilters((current) => ({
                ...current,
                Name: event.target.value,
                PageNumber: 1,
              }));
            }}
            className="h-11 border-gray-300 bg-white/60 pl-11"
          />
        </div>
        <Input
          aria-label="Search by code"
          placeholder="Search by code"
          value={filters.Code ?? ""}
          onChange={(event) =>
            setFilters((current) => ({
              ...current,
              Code: event.target.value,
              PageNumber: 1,
            }))
          }
          className="h-11 sm:w-56"
        />
        <Select
          value={
            filters.IsActive === undefined ? "all" : String(filters.IsActive)
          }
          onValueChange={(value) =>
            setFilters((current) => ({
              ...current,
              IsActive: value === "all" ? undefined : value === "true",
              PageNumber: 1,
            }))
          }
        >
          <SelectTrigger aria-label="Filter by status" className="h-11 sm:w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="true">Active</SelectItem>
            <SelectItem value="false">Inactive</SelectItem>
          </SelectContent>
        </Select>
        <Button
          onClick={() => openForm()}
          className="h-11 bg-[#5B9BD5] px-7 text-white hover:bg-[#4A8CC7]"
        >
          <Plus />
          Add Item
        </Button>
      </div>

      <div className="overflow-hidden rounded-2xl border border-sky-200 bg-white">
        <Table className="min-w-[680px]">
          <TableHeader>
            <TableRow className="border-sky-100 bg-[#E3F2FD] hover:bg-[#E3F2FD]">
              <TableHead className="h-14 w-[40%] px-6 text-sky-800">
                Name
              </TableHead>
              <TableHead className="w-[28%] px-6 text-sky-800">Code</TableHead>
              <TableHead className="px-6 text-sky-800">Status</TableHead>
              <TableHead className="w-28 px-6 text-center text-sky-800">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.publicId} className="border-slate-200">
                <TableCell className="h-14 px-6 font-medium text-slate-700">
                  {item.name}
                </TableCell>
                <TableCell className="px-6">
                  <Badge className="rounded border-[#D1E3F5] bg-sky-50 py-0 text-[#36577F]">
                    {item.code}
                  </Badge>
                </TableCell>
                <TableCell className="px-6">
                  <Badge
                    className={
                      item.status === "Active"
                        ? "rounded border-green-300 bg-green-100 text-green-700"
                        : "rounded border-gray-300 bg-gray-100 text-gray-600"
                    }
                  >
                    {item.status}
                  </Badge>
                </TableCell>
                <TableCell className="px-4">
                  <div className="flex justify-center gap-3">
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Edit ${item.name}`}
                      onClick={() => openForm(item)}
                      className="text-[#5B9BD5] hover:bg-sky-50"
                    >
                      <Pencil className="size-5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Delete ${item.name}`}
                      onClick={() => setDeletingItem(item)}
                      className="text-[#FF6B6B] hover:bg-red-50"
                    >
                      <Trash2 className="size-5" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && !listLoading && (
              <TableRow>
                <TableCell
                  colSpan={4}
                  className="py-10 text-center text-slate-500"
                >
                  No menu items found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <Pagination
          totalItems={totalRecords}
          itemsPerPage={filters.PageSize}
          currentPage={filters.PageNumber}
          onPageChange={(page) =>
            setFilters((current) => ({ ...current, PageNumber: page }))
          }
          onPageSizeChange={(size) => {
            setFilters((current) => ({
              ...current,
              PageSize: size,
              PageNumber: 1,
            }));
          }}
          label="menu items"
        />
      </div>

      <Dialog
        open={open}
        onOpenChange={(value) => {
          if (!value && !loading) closeForm();
        }}
      >
        <DialogContent className="!flex !max-h-[calc(100dvh-2rem)] !w-[calc(100%-2rem)] !max-w-2xl !flex-col !gap-0 overflow-hidden bg-white !p-0">
          <DialogHeader className="shrink-0 border-b px-4 py-4 pr-10 !text-left sm:px-6">
            <DialogTitle className="text-[#5B9BD5]">
              {editingId === null ? "Add Menu Item" : "Edit Menu Item"}
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              {editingId === null
                ? "Fill in details to add a new menu item."
                : "Update the details of this menu item."}
            </DialogDescription>
          </DialogHeader>
          <form
            noValidate
            autoComplete="off"
            onSubmit={saveItem}
            className="flex min-h-0 flex-col"
          >
            <div className="grid min-h-0 gap-4 overflow-y-auto px-4 py-4 sm:grid-cols-2 sm:px-6">
              <div>
                <InputFieldset
                  ref={nameInput}
                  id="menu-item-name"
                  name="menu-item-name"
                  autoComplete="off"
                  label="Menu Name"
                  aria-label="Menu Name"
                  required
                  aria-required="true"
                  placeholder="e.g. Peach Iced Tea (Beverage)"
                  value={form.name}
                  onChange={(event) => {
                    setForm({ ...form, name: event.target.value });
                    setErrors({ ...errors, name: "" });
                  }}
                  aria-invalid={!!errors.name}
                  aria-describedby={errors.name ? "menu-name-error" : undefined}
                  className="text-sm"
                />
                {errors.name && (
                  <p
                    id="menu-name-error"
                    role="alert"
                    className="mt-1 text-sm text-red-500"
                  >
                    {errors.name}
                  </p>
                )}
              </div>
              <div>
                <InputFieldset
                  ref={codeInput}
                  id="menu-item-code"
                  name="menu-item-code"
                  autoComplete="off"
                  label="Menu Code"
                  aria-label="Menu Code"
                  required
                  aria-required="true"
                  placeholder="e.g. BEV-ICED-TEA"
                  value={form.code}
                  onChange={(event) => {
                    setForm({ ...form, code: event.target.value });
                    setErrors({ ...errors, code: "" });
                  }}
                  aria-invalid={!!errors.code}
                  aria-describedby={errors.code ? "menu-code-error" : undefined}
                  className="text-sm"
                />
                {errors.code && (
                  <p
                    id="menu-code-error"
                    role="alert"
                    className="mt-1 text-sm text-red-500"
                  >
                    {errors.code}
                  </p>
                )}
              </div>
              <div className="sm:col-span-2">
                <InputFieldset
                  id="menu-item-description"
                  label="Description"
                  aria-label="Description"
                  placeholder="Enter menu item description"
                  value={form.description}
                  onChange={(event) =>
                    setForm({ ...form, description: event.target.value })
                  }
                />
              </div>
              <div className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3 sm:col-span-2">
                <Label htmlFor="menu-status">Status</Label>
                <div className="flex items-center gap-2">
                  <span className="text-sm">{form.status}</span>
                  <Switch
                    id="menu-status"
                    checked={form.status === "Active"}
                    onCheckedChange={(checked) =>
                      setForm({
                        ...form,
                        status: checked ? "Active" : "Inactive",
                      })
                    }
                  />
                </div>
              </div>
            </div>
            <DialogFooter className="!grid shrink-0 grid-cols-2 gap-2 border-t px-4 py-3 sm:!flex sm:px-6">
              <Button
                type="button"
                disabled={loading}
                onClick={closeForm}
                variant="outline"
                className="w-full sm:w-auto"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#5B9BD5] text-white hover:bg-[#4A8CC7] sm:w-auto"
              >
                <Save className="size-5" />
                Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deletingItem}
        onOpenChange={(value) => {
          if (!value && !loading) setDeletingItem(null);
        }}
        loading={loading}
        title="Delete Menu Item"
        description={
          <>
            Are you sure you want to delete{" "}
            <strong>{deletingItem?.name}</strong>?
          </>
        }
        confirmText="Yes, Delete"
        onConfirm={confirmDelete}
      />
    </div>
  );
}
